import { faClock } from "@fortawesome/duotone-light-svg-icons"
import { faBan, faCheck } from "@fortawesome/free-solid-svg-icons"
import { faXmark } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  JoonUIColor,
  Loader,
  Modal,
  QuotesIcon,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useState } from "react"

import CancelSessionButton from "./buttons/CancelSessionButton"
import CopySessionLinkButton from "./buttons/CopySessionLinkButton"
import JoinSessionButton from "./buttons/JoinSessionButton"
import LateCancelButton from "./buttons/LateCancelButton"
import NoShowButton from "./buttons/NoShowButton"
import RescheduleButton from "./buttons/RescheduleButton"
import ScheduleMakeupButton from "./buttons/ScheduleMakeupButton"
import ScheduleNextSessionButton from "./buttons/ScheduleNextSessionButton"
import GenerateNotesSection from "./GenerateNotesSection"
import useSession from "./hooks/useSession"
import useSessionModalStore from "./hooks/useSessionModalStore"
import AddAttachmentButton from "../../../components/attachments/AddAttachmentButton"
import AttachmentItem from "../../../components/attachments/AttachmentItem"
import CustomTextArea2 from "../../../components/textarea/TextArea2"
import { notesPlaceholderText } from "../../../constants"
import { createResource } from "../../../networking/resources/api"
import {
  useUpdateSessionBookingMutation,
  useUpdateSessionNotesMutation,
} from "../../../networking/sessions/mutations"
import {
  useSessionBillingPeriodsQuery,
  useSessionBookingQuery,
} from "../../../networking/sessions/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { formatDate } from "../../../util/dates"
import useCurrentClient from "../hooks/useCurrentClient"

const SessionModal = () => {
  const {
    isOpen,
    onClose,
    sessionBookingId,
    sessionMarkedAsCompleted,
    setSessionMarkedAsCompleted,
    isWritingManualNotes,
  } = useSessionModalStore()
  const { currentClientProfile } = useCurrentClient()
  const updateSessionNotesMutation = useUpdateSessionNotesMutation()

  const { data: sessionBooking, isLoading: sessionBookingLoading } =
    useSessionBookingQuery(Number(sessionBookingId))
  const { data: billingPeriods } = useSessionBillingPeriodsQuery()

  const billingPeriod = billingPeriods?.find((period) =>
    period.bookings.some((booking) => booking.id === sessionBookingId)
  )

  const [sessionSummary, setSessionSummary] = useState(
    sessionBooking?.session_summary_notes?.note
  )
  const [sessionNotes, setSessionNotes] = useState(
    sessionBooking?.session_notes?.note
  )
  const [hasChangedNotes, setHasChangedNotes] = useState(false)
  const [hasChangedSummary, setHasChangedSummary] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const { sessionIsWithin24Hours, sessionIsPast } = useSession(sessionBooking)

  const updateSessionBookingMutation = useUpdateSessionBookingMutation()

  const sessionDuration = dayjs(sessionBooking?.end_datetime).diff(
    dayjs(sessionBooking?.start_datetime),
    "minutes"
  )

  const isSummaryVisibleToClient =
    sessionBooking?.session_summary_notes?.visibility_role === null

  // Set session notes when session booking changes, if not set when rendered
  useEffect(() => {
    if (!sessionBooking) return

    !hasChangedSummary &&
      setSessionSummary(sessionBooking.session_summary_notes?.note || "")
    !hasChangedNotes &&
      setSessionNotes(
        sessionBooking.session_notes?.note || notesPlaceholderText
      )
  }, [sessionBooking, hasChangedNotes, hasChangedSummary])

  if (sessionBookingLoading)
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        style={{ padding: SPACING.space2 }}
      >
        <Loader size={20} color={JoonUIColor.icon.accent} thickness={2} />
      </Modal>
    )

  if (!sessionBooking) return null

  const showSessionActions =
    (sessionIsWithin24Hours || sessionIsPast) &&
    !isSummaryVisibleToClient &&
    !sessionMarkedAsCompleted &&
    !sessionBooking?.marked_late_cancellation &&
    !sessionBooking?.marked_no_show

  const updateSessionNotes = () => {
    if (sessionBookingLoading) return
    if (sessionNotes === sessionBooking?.session_notes?.note) return
    if (!sessionBooking?.session_notes?.id) return
    updateSessionNotesMutation.mutate({
      noteId: sessionBooking.session_notes.id,
      note: sessionNotes,
      sessionBookingId: sessionBooking.id,
    })
    setHasChangedNotes(false)
  }

  const sendSummaryToClient = async () => {
    if (!sessionBooking.session_summary_notes?.id) return
    setHasChangedNotes(false)
    setHasChangedSummary(false)

    if (attachments.length > 0) {
      const promises = attachments.map(async (attachment) => {
        const formData = new FormData()
        formData.append("resource_url", attachment)
        formData.append("description", attachment.type)
        formData.append("title", attachment.name)

        return createResource(formData)
      })
      const attachmentIds = await Promise.all(promises).then((res) =>
        res.map((r) => r.id)
      )

      updateSessionBookingMutation.mutate({
        sessionBookingId: sessionBooking.id,
        resourceIds: attachmentIds,
      })
    }

    updateSessionNotesMutation.mutate({
      noteId: sessionBooking.session_notes?.id!,
      note: sessionNotes,
      sessionBookingId: sessionBooking.id,
    })
    updateSessionNotesMutation.mutate({
      noteId: sessionBooking.session_summary_notes?.id,
      visibilityRole: null,
      sessionBookingId: sessionBooking.id,
      note: sessionSummary,
    })
    trackAnalyticEvent(ANALYTIC_EVENTS.POST_SESSION_FLOW_SEND_CLIENT_SUMMARY, {
      has_attachment: attachments.length > 0,
      auto_scheduled: sessionBooking?.auto_scheduled,
    })
  }

  const handleMarkAsCompleted = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.MARK_SESSION_COMPLETED, {
      auto_scheduled: sessionBooking?.auto_scheduled,
    })
    setSessionMarkedAsCompleted(true)
    updateSessionNotes()
  }

  const handleClose = () => {
    const confirmationMessage =
      "Are you sure you want to close? Any unsaved changes will be lost."
    if (!hasChangedNotes) onClose()
    else if (window.confirm(confirmationMessage)) onClose()
  }

  const handleRemoveAttachment = (index: number) => {
    const newAttachments = [...attachments]
    newAttachments.splice(index, 1)
    setAttachments(newAttachments)
  }

  return (
    <Modal
      isOpen={isOpen}
      mandatory={!isSummaryVisibleToClient}
      onClose={handleClose}
      style={{ width: "min(1150px, 100vw)", height: "min(90vh, 900px)" }}
    >
      <button onClick={handleClose} className="absolute top-2 right-3 p-2">
        <FontAwesomeIcon
          icon={faXmark}
          fontSize={20}
          color={JoonUIColor.icon.neutral}
        />
      </button>
      <div className="flex flex-row items-center justify-between p-6 border-b border-borderPrimary">
        <div className="flex flex-col gap-2">
          <Typography variant="h3">
            {currentClientProfile?.user.name}{" "}
            {currentClientProfile?.user.last_name}
          </Typography>
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-row items-center gap-1 p-1 py-[2px] bg-indigo-100 rounded-md">
              <FontAwesomeIcon icon={faClock} color={JoonUIColor.icon.accent} />
              <Typography variant="bodySmall">{sessionDuration} min</Typography>
            </div>
            <Typography
              variant="bodySmall"
              className="flex flex-row items-center gap-2"
              color={JoonUIColor.text.secondary}
            >
              <div>
                {formatDate(new Date(sessionBooking.start_datetime), "MMM D")}
              </div>
              <div>|</div>
              <div>
                {formatDate(
                  new Date(sessionBooking.start_datetime),
                  "h:mma",
                  false
                )}{" "}
                -{" "}
                {formatDate(
                  new Date(sessionBooking.end_datetime),
                  "h:mma",
                  false
                )}
              </div>
            </Typography>
          </div>
        </div>
        {isSummaryVisibleToClient ? (
          <div className="flex flex-row items-center gap-1 p-1 px-2 rounded-full bg-viridian-100">
            <FontAwesomeIcon
              icon={faCheck}
              color={JoonUIColor.semantic.success}
            />
            <Typography variant="bodySmall">Completed</Typography>
          </div>
        ) : sessionBooking.marked_no_show ||
          sessionBooking.marked_late_cancellation ? (
          <div className="flex flex-row items-center gap-1 px-2 py-1 bg-red-100 rounded-full">
            <FontAwesomeIcon
              icon={faBan}
              color={JoonUIColor.semantic.destructive}
            />
            <Typography variant="bodySmall" style={{ whiteSpace: "nowrap" }}>
              {sessionBooking.marked_no_show ? "No-show" : "Late cancel"}
            </Typography>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-2">
            <CancelSessionButton session={sessionBooking} />
            <RescheduleButton session={sessionBooking} />
            <CopySessionLinkButton session={sessionBooking} />
            <JoinSessionButton session={sessionBooking} />
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 gap-6 p-6">
        <div className="flex flex-col">
          <Typography variant="bodyBold">Discussion topic</Typography>
          <div className="flex items-center w-full gap-2 p-4 rounded-md bg-bgXLightGray">
            {sessionBooking.topic_to_discuss_notes?.note && <QuotesIcon />}
            <Typography variant="bodySmall">
              {sessionBooking.topic_to_discuss_notes?.note ||
                "Topic not yet set by client"}
            </Typography>
          </div>
        </div>
        <div className="flex flex-row flex-1 gap-6">
          <div className="flex flex-col flex-1 gap-1">
            <div className="flex flex-row items-center justify-between">
              <Typography variant="bodyBold">Internal session notes</Typography>
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.secondary}
              >
                Only visible to you
              </Typography>
            </div>
            <div className="flex flex-col flex-1 gap-1">
              <CustomTextArea2
                name="session-notes"
                value={sessionNotes || ""}
                onChange={(e) => {
                  setSessionNotes(e.target.value)
                  setHasChangedNotes(true)
                }}
                style={{
                  padding: SPACING.space2,
                  flex: 1,
                  background: JoonUIColor.background.xlightGray,
                }}
              />
              {hasChangedNotes && (
                <Button
                  size="small"
                  text="Save"
                  className="ml-auto"
                  isLoading={updateSessionNotesMutation.isLoading}
                  buttonType="secondary"
                  onClick={updateSessionNotes}
                />
              )}
            </div>
          </div>
          {(sessionMarkedAsCompleted || isSummaryVisibleToClient) && (
            <div className="flex flex-col flex-1 gap-1">
              <div className="flex flex-row items-center justify-between">
                <Typography variant="bodyBold">Summary for client</Typography>
                {isSummaryVisibleToClient && (
                  <div className="flex flex-row items-center gap-1 p-1 px-2 rounded-full bg-viridian-100">
                    <FontAwesomeIcon
                      icon={faCheck}
                      color={JoonUIColor.semantic.success}
                    />
                    <Typography variant="bodySmall">Sent to client</Typography>
                  </div>
                )}
              </div>
              {sessionSummary || isWritingManualNotes ? (
                <CustomTextArea2
                  name="session-summary"
                  value={sessionSummary || ""}
                  onChange={(e) => {
                    setSessionSummary(e.target.value)
                    setHasChangedSummary(true)
                  }}
                  disabled={isSummaryVisibleToClient}
                  style={{
                    padding: SPACING.space2,
                    flex: 1,
                    background: JoonUIColor.background.xlightGray,
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center flex-1 gap-1 p-4 border rounded-lg bg-bgXLightGray border-borderPrimary">
                  <GenerateNotesSection
                    sessionBookingId={sessionBookingId!}
                    setSummaryNotes={setSessionSummary}
                  />
                </div>
              )}
              {sessionBooking.resources.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {sessionBooking.resources.map((file) => (
                    <AttachmentItem
                      name={file.title}
                      url={file.resource_url}
                      key={file.id}
                    />
                  ))}
                </div>
              ) : attachments.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {attachments.map((file, index) => (
                    <AttachmentItem
                      name={file.name}
                      url={URL.createObjectURL(file)}
                      key={file.name}
                      onRemove={
                        isSummaryVisibleToClient
                          ? undefined
                          : () => handleRemoveAttachment(index)
                      }
                    />
                  ))}
                </div>
              ) : (
                !isSummaryVisibleToClient && (
                  <AddAttachmentButton
                    attachments={attachments}
                    setAttachments={setAttachments}
                  />
                )
              )}
            </div>
          )}
        </div>
      </div>
      {showSessionActions ? (
        <div className="flex flex-col gap-2 px-6 py-4 bg-green-100 border-t border-borderPrimary">
          <Typography variant="body" textAlign="center">
            Mark session as:
          </Typography>
          <div className="flex flex-row gap-2">
            <NoShowButton session={sessionBooking} />
            <LateCancelButton session={sessionBooking} />
            <Button
              text="Completed"
              fullWidth
              onClick={handleMarkAsCompleted}
            />
          </div>
        </div>
      ) : sessionBooking.marked_no_show ||
        sessionBooking.marked_late_cancellation ? (
        <div className="flex flex-col gap-2 px-6 py-4 border-t border-borderPrimary">
          <ScheduleMakeupButton
            session={sessionBooking}
            billingPeriod={billingPeriod!}
          />
        </div>
      ) : (
        (sessionMarkedAsCompleted || isSummaryVisibleToClient) && (
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-borderPrimary">
            <ScheduleNextSessionButton />
            {!isSummaryVisibleToClient && (
              <Button
                text="Send summary to client"
                disabled={!sessionSummary}
                onClick={sendSummaryToClient}
              />
            )}
          </div>
        )
      )}
    </Modal>
  )
}

export default SessionModal
